import React from 'react';

import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { setIsTldAlertModalOpen, setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  canJobBeModified,
  createDataCyForJob,
  hasJobBeenModified,
  isJobAtCapacity,
  JmrJobTypes,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import { areConflictsResolved, getJobError } from 'src/utils/placementSelectionUtils';
import { toggleJob } from 'src/utils/placementSelectionUtils';

import ConflictPopover from './conflictPopover';
import ModifyBtn from './modifyBtn';

interface SingleShiftJobRowProps {
  row: TableDataRow;
  selectedJobRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  jobsExceedingRestrictionsInAggregate: Set<string>;
  exceededRestrictionIds: Set<number>;
  onClickModify: (row: TableDataRow, shiftId?: string) => void;
  focusSelection: (resultId: string) => void;
}

const SingleShiftJobRow = ({
  row,
  selectedJobRefs,
  jobsExceedingRestrictionsInAggregate,
  exceededRestrictionIds,
  onClickModify,
  focusSelection,
}: SingleShiftJobRowProps) => {
  const { jmrFilter, jmrResults, backgroundFormData, tldAlertModalAcknowledged } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const dispatch = useAppDispatch();
  const isT2Experience = useIsT2Experience();

  const setTotalHours = (id: string, totalHours: string) => {
    dispatch(
      setJmrResults(jmrResults.map((jmrResult) => (jmrResult.id === id ? { ...jmrResult, totalHours } : jmrResult))),
    );
  };

  const onChecked = (resultId: string) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModalAcknowledged) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId,
        }),
      );
    } else {
      toggleJob(resultId, jmrResults, dispatch);
      focusSelection(resultId);
    }
  };

  const jobError = getJobError(row, jmrResults, backgroundFormData, jobsExceedingRestrictionsInAggregate);
  const hasJobError = !!jobError;

  return (
    <TableRow highlightOnHover={true}>
      <TableCell>
        <Column spacing="200">
          <Text type="b300">{row.name}</Text>
          <Text type="b200" color="secondary">
            {row.jobType}
          </Text>
        </Column>
      </TableCell>
      <TableCell>
        {!!row.shiftCode && (
          <Column spacing="200">
            <Text type="b300">{row.shiftCode}</Text>
            <Text type="b200" color="secondary">
              {`${row?.jobCapacity || 0} hrs capacity`}
            </Text>
          </Column>
        )}
      </TableCell>
      <TableCell>
        <Column spacing="200">
          <ConflictPopover jobMatch={row} {...{ exceededRestrictionIds }} />
          <Text type="b200" color="secondary">
            {areConflictsResolved(row.conflicts) ? 'Full Shift' : `Maximum hrs: ${row.maximumHoursAssociateCanDo}`}
          </Text>
        </Column>
      </TableCell>
      <TableCell>
        <ModifyBtn
          row={row}
          hasBeenModified={isT2Experience ? !!row.modificationNotes?.trim().length : hasJobBeenModified(row.conflicts)}
          canBeModified={
            isT2Experience ? row.tierTwoPlusJobModified : canJobBeModified(row.conflicts, row.shifts, row.jobCapacity)
          }
          onClickModify={onClickModify}
        />
      </TableCell>
      <TableCell>
        {row.maximumHoursAssociateCanDo === 0 || isJobAtCapacity(row.shifts, row.jobCapacity) ? (
          <Tooltip
            title={
              isJobAtCapacity(row.shifts, row.jobCapacity)
                ? 'Capacity is full.'
                : 'Apply job modifications to enable selection of this job.'
            }
            position="top"
          >
            <Checkbox disabled={true} />
          </Tooltip>
        ) : (
          <Checkbox
            disabled={
              (!row.isChecked && isT2Experience && jmrResults.some((r) => r.isChecked)) ||
              (isT2Experience && row.tierTwoPlusJobModified && !row.modificationNotes?.trim().length)
            }
            checked={row.isChecked}
            onChange={() => onChecked(row.id)}
            data-cy={`${createDataCyForJob(row.name)}-checkbox`}
            aria-label={`${row.name} Checkbox`}
          />
        )}
      </TableCell>
      <TableCell width={80} alignmentVertical="top">
        <div className="jobMatchesTable__input-wrapper">
          {row.isChecked ? (
            <Input
              ref={(ref) => (ref ? selectedJobRefs.current.set(row.id, ref) : selectedJobRefs.current.delete(row.id))}
              type="text"
              value={row.totalHours.toString()}
              onChange={(totalHours: string) => setTotalHours(row.id, totalHours)}
              error={hasJobError}
              errorMessage={jobError}
              size="small"
              placeholder="hrs..."
              data-cy={`${createDataCyForJob(row.name)}-totalHours`}
            />
          ) : (
            ''
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default SingleShiftJobRow;
